import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, MapPin, Globe, LayoutGrid, Mic2, ArrowRight, PlayCircle, Hash, Loader2, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { getPublicBrand } from '../../lib/apiClient';

export default function BrandPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedQaId, setExpandedQaId] = useState(null);

  useEffect(() => {
      async function load() {
          try {
              setLoading(true);
              const res = await getPublicBrand(slug);
              setData(res);
          } catch (e) {
              console.error(e);
              setError(e);
          } finally {
              setLoading(false);
          }
      }
      load();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center text-white"><Loader2 className="w-8 h-8 animate-spin text-primary"/></div>;
  if (error || !data) return <div className="min-h-screen bg-background flex items-center justify-center text-white">Brand not found</div>;

  const { brand, topics, recent_insights, recent_qa } = data;

  // Derive stats
  const stats = {
      insights: topics.reduce((acc, t) => acc + (t.insight_count || 0), 0), // Approx total from top topics
      topics: topics.length,
      qa: recent_qa?.length || 0
  };

  return (
    <div className="min-h-screen bg-background text-white font-sans pb-20">
      
      {/* Hero / Header */}
      <div className="relative bg-surface border-b border-white/5 pt-24 pb-12 px-6">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-blue-900/10 to-transparent pointer-events-none" />
        
        <div className="container mx-auto max-w-6xl relative z-10 flex flex-col md:flex-row items-start gap-8">
          {/* Logo */}
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-white/10 flex items-center justify-center text-3xl font-bold text-white shadow-2xl border border-white/10 overflow-hidden">
            {brand.logo_url ? <img src={brand.logo_url} alt={brand.name} className="w-full h-full object-cover"/> : brand.name.substring(0, 2).toUpperCase()}
          </div>
          
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-white mb-2">{brand.name}</h1>
            <p className="text-xl text-slate-300 mb-6 max-w-2xl leading-relaxed">{brand.description}</p>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500 mb-8">
              {brand.website_url && (
                  <span className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    <a href={brand.website_url.startsWith('http') ? brand.website_url : `https://${brand.website_url}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                      {brand.website_url}
                    </a>
                  </span>
              )}
              {/* Location isn't in DB schema yet */}
            </div>

            <div className="flex flex-wrap gap-4">
              {brand.website_url && (
                  <a href={brand.website_url.startsWith('http') ? brand.website_url : `https://${brand.website_url}`} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-gradient-primary text-white font-bold rounded-lg shadow-glow-blue hover:scale-105 transition-transform flex items-center gap-2">
                    Visit Website
                    <ExternalLink className="w-4 h-4" />
                  </a>
              )}
            </div>
          </div>

          {/* Stats Card */}
          <div className="w-full md:w-auto bg-surface/50 border border-white/10 rounded-xl p-6 min-w-[200px]">
            <div className="grid grid-cols-3 md:grid-cols-1 gap-6">
              <div>
                <div className="text-3xl font-bold text-white mb-1">{stats.insights}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Insights</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white mb-1">{stats.topics}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Top Topics</div>
              </div>
              {stats.qa > 0 && (
                <div>
                  <div className="text-3xl font-bold text-white mb-1">{stats.qa}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Q&A</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-6 py-12 grid lg:grid-cols-3 gap-12">
        
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-12">
          
          {/* Featured Insights */}
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-primary" />
                Recent Insights
              </h2>
            </div>
            <div className="space-y-4">
              {recent_insights.map(insight => (
                <Link key={insight.id} to={`/topics/${insight.topics?.slug || 'general'}/insights/${insight.id}`} className="block p-6 bg-surface border border-white/5 rounded-xl hover:border-primary/30 transition-all group">
                  <div className="flex items-start justify-between mb-2">
                    <span className="px-2 py-1 rounded-md bg-white/5 text-xs text-primary font-medium border border-primary/10">
                      {insight.topics?.title || 'General'}
                    </span>
                    <span className="text-xs text-slate-600">{new Date(insight.created_at).toLocaleDateString()}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-primary transition-colors">{insight.title}</h3>
                  <p className="text-slate-400 text-sm">{insight.hook}</p>
                </Link>
              ))}
              {recent_insights.length === 0 && <div className="text-slate-500">No public insights yet.</div>}
            </div>
          </section>

          {/* Recent Q&A */}
          {recent_qa && recent_qa.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-primary" />
                  Recent Q&A
                </h2>
              </div>
              <div className="space-y-4">
                {recent_qa.map(qa => (
                  <article 
                    key={qa.id}
                    className="bg-surface border border-white/10 rounded-xl overflow-hidden hover:border-primary/30 transition-all"
                  >
                    <button
                      onClick={() => setExpandedQaId(expandedQaId === qa.id ? null : qa.id)}
                      className="w-full text-left p-5 flex items-start justify-between gap-4 group"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {qa.topics && (
                            <span className="px-2 py-0.5 rounded-md bg-white/5 text-xs text-primary font-medium border border-primary/10">
                              {qa.topics.title}
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-white text-lg group-hover:text-primary transition-colors pr-4">
                          {qa.question}
                        </h3>
                        {expandedQaId !== qa.id && (
                          <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                            {qa.answer.substring(0, 120)}...
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-slate-500 group-hover:text-primary transition-colors">
                        {expandedQaId === qa.id ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </div>
                    </button>

                    {expandedQaId === qa.id && (
                      <div className="px-5 pb-5 border-t border-white/5 pt-4">
                        <div className="text-slate-300 leading-relaxed mb-4">
                          {qa.answer}
                        </div>
                        {qa.insights && (
                          <Link
                            to={`/topics/${qa.topics?.slug || 'general'}/insights/${qa.insights.id}`}
                            className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Read full insight
                          </Link>
                        )}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Recent Episodes - TODO: Fetch real episodes from API */}
          {/* 
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Mic2 className="w-5 h-5 text-secondary" />
                Recent Episodes
              </h2>
            </div>
            ...
          </section> 
          */}

        </div>

        {/* Sidebar - Topics */}
        <div className="space-y-8">
          <section>
             <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Hash className="w-5 h-5 text-slate-400" />
                Topic Footprint
             </h2>
             <div className="grid gap-3">
                {topics.map((t, i) => (
                   <Link key={i} to={`/topics/${t.topics.slug}`} className="flex items-center justify-between p-4 rounded-lg bg-surface border border-white/5 hover:border-white/20 transition-colors group">
                      <span className="font-medium text-slate-300 group-hover:text-white">{t.topics.title}</span>
                      <span className="text-xs font-mono bg-white/5 px-2 py-1 rounded text-slate-500 group-hover:text-white transition-colors">
                        {t.insight_count}
                      </span>
                   </Link>
                ))}
             </div>
          </section>

          {/* Creator CTA */}
          <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-white/10 text-center">
             <h3 className="font-bold text-white mb-2">Claim this profile?</h3>
             <p className="text-sm text-slate-400 mb-6">
                Verify ownership to manage your brand presence and access deep analytics.
             </p>
             <Link to="/signup" className="block w-full py-3 bg-white text-black font-bold rounded-lg hover:bg-slate-200 transition-colors text-sm">
                Claim Brand Profile
             </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
